import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import { WHATSAPP_URL } from '@/constants/whatsapp';
import { getOrganizationSchema } from '@/constants/schema';

const SITE_URL = import.meta.env.VITE_SITE_URL as string;

const PAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    getOrganizationSchema(),
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/bulk-wholesale#webpage`,
      url: `${SITE_URL}/bulk-wholesale`,
      name: 'Bulk Wholesale Gloves Oman GCC | Volume Pricing | omandisposables.com',
      description: "Oman's experienced wholesale glove supplier. 1,000–50,000+ boxes delivered across GCC. Volume pricing and supply contracts for medical, food, lab & industrial gloves.",
      inLanguage: ['en', 'ar'],
      isPartOf: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Bulk & Wholesale Gloves', item: `${SITE_URL}/bulk-wholesale` },
      ],
    },
    {
      '@type': 'Service',
      '@id': `${SITE_URL}/bulk-wholesale/#service`,
      name: 'Wholesale Disposable Gloves Supply — Oman & GCC',
      url: `${SITE_URL}/bulk-wholesale`,
      description: "Oman's experienced wholesale glove supplier. 1,000 to 50,000+ boxes delivered across GCC. Volume pricing and supply contracts.",
      provider: {
        '@type': 'Organization',
        name: 'omandisposables.com',
        url: SITE_URL,
        address: { '@type': 'PostalAddress', addressCountry: 'OM', addressLocality: 'Muscat' },
      },
      areaServed: [
        { '@type': 'Country', name: 'Oman' },
        { '@type': 'Country', name: 'United Arab Emirates' },
        { '@type': 'Country', name: 'Saudi Arabia' },
        { '@type': 'Country', name: 'Kuwait' },
        { '@type': 'Country', name: 'Bahrain' },
        { '@type': 'Country', name: 'Qatar' },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Wholesale Gloves Catalog',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Medical & Examination Gloves' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Food Grade Gloves' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Laboratory Gloves' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Industrial PPE Gloves' } },
        ],
      },
    },
  ],
};

const BENEFITS = [
  { icon: 'ri-percent-line', en: 'Volume Discounts', ar: 'خصومات الحجم', desc: 'Save up to 10% on large orders. Enterprise contracts negotiated directly.' },
  { icon: 'ri-truck-line', en: 'Fast GCC Delivery', ar: 'توصيل سريع للخليج', desc: 'Shipping from Oman to all GCC countries within 2–7 business days.' },
  { icon: 'ri-contract-line', en: 'Supply Contracts', ar: 'عقود التوريد', desc: 'Annual and quarterly supply agreements with locked-in pricing available.' },
  { icon: 'ri-award-line', en: 'Quality Stock', ar: 'مخزون عالي الجودة', desc: 'All products meet high quality standards — consistently supplied and audit-ready from our Oman warehouse.' },
  { icon: 'ri-box-3-line', en: 'Mixed Product Orders', ar: 'طلبات منتجات متعددة', desc: 'Combine medical, food, lab and industrial gloves in a single shipment.' },
  { icon: 'ri-customer-service-2-line', en: '24hr WhatsApp Response', ar: 'استجابة واتساب 24 ساعة', desc: 'Dedicated wholesale team — quote response within 24 hours, guaranteed.' },
];

const PRODUCTS_BRIEF = [
  { name: 'Medical & Examination Gloves', nameAr: 'القفازات الطبية وقفازات الفحص', icon: 'ri-heart-pulse-line', to: '/medical-gloves' },
  { name: 'Food Grade Gloves', nameAr: 'قفازات المواد الغذائية', icon: 'ri-restaurant-2-line', to: '/food-grade-gloves' },
  { name: 'Laboratory Gloves', nameAr: 'قفازات المختبرات', icon: 'ri-flask-line', to: '/lab-gloves' },
  { name: 'Industrial PPE Gloves', nameAr: 'قفازات الوقاية الصناعية', icon: 'ri-building-2-line', to: '/industrial-gloves' },
];

export default function BulkWholesalePage() {
  return (
    <main>
      <title>Bulk Wholesale Gloves Oman GCC | Volume Pricing | omandisposables.com</title>
      <meta name="description" content="Oman's experienced wholesale glove supplier. 1,000–50,000+ boxes delivered across GCC. Volume pricing and supply contracts for medical, food, lab & industrial gloves." />
      <meta name="keywords" content="wholesale gloves Oman, bulk gloves GCC, volume gloves pricing Muscat, supply contract gloves, disposable gloves wholesale supplier" />
      <link rel="canonical" href={`${SITE_URL}/bulk-wholesale`} />
      <meta property="og:title" content="Bulk Wholesale Gloves Oman GCC | omandisposables.com" />
      <meta property="og:description" content="Oman's experienced wholesale glove supplier. Volume pricing and supply contracts across the GCC." />
      <meta property="og:url" content={`${SITE_URL}/bulk-wholesale`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://public.readdy.ai/ai/img_res/623276ce8b8bcea7b193f3a67538a843.jpg" />
      <meta property="og:site_name" content="omandisposables.com" />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Bulk Wholesale Gloves Oman GCC | omandisposables.com" />
      <meta name="twitter:description" content="1,000–50,000+ boxes. Volume pricing and supply contracts for wholesale gloves across Oman & GCC." />
      <meta name="twitter:image" content="https://readdy.ai/api/search-image?query=large%20commercial%20warehouse%20full%20of%20stacked%20cardboard%20boxes%20of%20disposable%20gloves%20on%20industrial%20shelving%2C%20forklift%20operating%20in%20background%2C%20organized%20bulk%20wholesale%20storage%20facility%20with%20overhead%20lighting%20and%20wide%20aisles&width=1200&height=630&seq=bulk-hero-og&orientation=landscape" />
      <meta name="twitter:site" content="@omandisposables" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(PAGE_SCHEMA) }}
      />

      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 pb-16 bg-navy overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=large%20commercial%20warehouse%20full%20of%20stacked%20cardboard%20boxes%20of%20disposable%20gloves%20on%20industrial%20shelving%2C%20forklift%20operating%20in%20background%2C%20organized%20bulk%20wholesale%20storage%20facility%20with%20overhead%20lighting%20and%20wide%20aisles&width=1920&height=600&seq=bulk-hero-01&orientation=landscape"
            alt="Wholesale Gloves Oman GCC - omandisposables.com"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/95 to-navy/65" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div className="inline-flex items-center gap-2 bg-brand-green/20 border border-brand-green/40 text-brand-green text-sm font-semibold px-4 py-2 rounded-full mb-6">
            🏅 Wholesale Excellence — Rooted in Oman
          </div>
          <h1 className="font-heading font-black text-white text-4xl md:text-5xl lg:text-6xl mb-3">
            Bulk &amp; Wholesale Gloves
          </h1>
          <p className="font-arabic text-white/80 text-2xl md:text-3xl font-bold mb-4" dir="rtl">
            القفازات بالجملة والكميات الكبيرة
          </p>
          <p className="text-white/90 text-base md:text-lg max-w-2xl mb-4">
            Oman&apos;s experienced wholesale glove supplier. 1,000 to 50,000+ boxes delivered across
            the GCC. Volume pricing and supply contracts — all from one trusted source.
          </p>
          <p className="font-arabic text-white/80 text-base max-w-2xl mb-8 text-right" dir="rtl">
            أكثر موردي القفازات خبرةً في عُمان. توصيل من 1,000 إلى أكثر من 50,000 صندوق في جميع أنحاء الخليج.
            أسعار حجم وعقود توريد — كل ذلك من مصدر موثوق واحد.
          </p>
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp text-base px-8 py-4 rounded-full shadow-xl hover:scale-105 transition-transform inline-flex whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-xl" />
              💬 WhatsApp for Wholesale Quote
            </a>
            <Link
              to="/request-quote"
              className="bg-white/10 border border-white/20 text-white font-heading font-semibold px-8 py-4 rounded-full hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              📋 Submit a Formal Quote Request
            </Link>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <div className="bg-brand-green py-3 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center gap-6 text-white text-sm font-semibold">
          {['500+ Clients', '10M+ Gloves Supplied', 'GCC-Wide Delivery', '24hr Response'].map((t) => (
            <span key={t} className="flex items-center gap-1">
              <i className="ri-checkbox-circle-fill" />{t}
            </span>
          ))}
        </div>
      </div>

      {/* Benefits */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="section-title-en text-3xl md:text-4xl mb-2">Why Buy Wholesale from omandisposables.com?</h2>
            <p className="section-title-ar text-2xl md:text-3xl" dir="rtl">لماذا تشتري بالجملة من omandisposables.com؟</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b) => (
              <div key={b.en} className="bg-white rounded-2xl border border-gray-100 p-6 hover:border-brand-green/30 transition-all">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-navy/5 mb-4">
                  <i className={`${b.icon} text-2xl text-navy`} />
                </div>
                <h3 className="font-heading font-bold text-navy text-base mb-0.5">{b.en}</h3>
                <p className="font-arabic text-gray-600 text-sm mb-3" dir="rtl">{b.ar}</p>
                <p className="text-gray-600 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories Briefing */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-10">
            <h2 className="section-title-en text-2xl md:text-3xl mb-2">Available Product Categories</h2>
            <p className="section-title-ar text-xl md:text-2xl" dir="rtl">فئات المنتجات المتاحة</p>
            <p className="text-gray-600 text-sm mt-2">Mix and match across product lines in a single wholesale order</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {PRODUCTS_BRIEF.map((p) => (
              <Link
                key={p.name}
                to={p.to}
                className="flex flex-col items-center text-center p-6 bg-gray-50 rounded-2xl border-2 border-gray-100 hover:border-brand-green/50 hover:bg-white transition-all group cursor-pointer"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-navy/5 mb-4 group-hover:bg-brand-green/10 transition-colors">
                  <i className={`${p.icon} text-3xl text-navy group-hover:text-brand-green transition-colors`} />
                </div>
                <p className="font-heading font-bold text-navy text-sm">{p.name}</p>
                <p className="font-arabic text-gray-500 text-xs mt-1" dir="rtl">{p.nameAr}</p>
                <span className="mt-3 text-xs text-brand-green font-semibold font-heading flex items-center gap-1">
                  View Range <i className="ri-arrow-right-line" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-white text-center">
        <div className="container-max">
          <div className="inline-flex items-center gap-2 bg-brand-green/10 border border-brand-green/30 text-brand-green text-sm font-semibold px-4 py-2 rounded-full mb-6">
            🏅 جودة موثوقة. راسخون في عُمان. | Quality. Rooted in Oman.
          </div>
          <h2 className="font-heading font-black text-navy text-3xl md:text-4xl mb-3">
            Ready to Place Your Wholesale Order?
          </h2>
          <p className="font-arabic text-gray-600 text-xl font-bold mb-6" dir="rtl">
            هل أنت مستعد لتقديم طلب الجملة الخاص بك؟
          </p>
          <p className="text-gray-600 text-base max-w-lg mx-auto mb-8">
            WhatsApp our team for an instant quote or fill out the form — we respond within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp text-base px-8 py-4 rounded-full shadow-xl hover:scale-105 transition-transform w-full sm:w-auto justify-center"
            >
              <i className="ri-whatsapp-line text-xl" />
              💬 WhatsApp for Wholesale Price
            </a>
            <Link
              to="/request-quote"
              className="bg-navy text-white font-heading font-bold px-8 py-4 rounded-full hover:bg-navy/90 transition-all whitespace-nowrap cursor-pointer w-full sm:w-auto flex items-center justify-center gap-2"
            >
              <i className="ri-file-list-3-line" />
              📋 Request a Formal Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
